import { DueAtRangeType } from '@/features/maintenance-data/hooks/useMaintenanceDataQueryParams';

/**
 * A date range as consumed/produced by the rio-uikit DayPicker (`mode="range"`),
 * whose value shape is `{ from?: Date; to?: Date }`.
 */
export interface DateRangeValue {
    from?: Date;
    to?: Date;
}

/** The due-at presets that resolve to a concrete, client-computed date range. */
export type ComputablePreset = Exclude<DueAtRangeType, 'CUSTOM'>;

const startOfDay = (date: Date): Date => new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);

const endOfDay = (date: Date): Date => new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);

const addDays = (date: Date, days: number): Date => {
    const next = new Date(date);
    next.setDate(next.getDate() + days);
    return next;
};

/**
 * Monday of the ISO week containing [date]. ISO weeks start on Monday; JS `getDay()`
 * returns 0 for Sunday, so we map it to a Monday-based offset.
 */
const startOfIsoWeek = (date: Date): Date => {
    const mondayOffset = (date.getDay() + 6) % 7;
    return startOfDay(addDays(date, -mondayOffset));
};

/**
 * Resolves a due-at preset to a concrete date range, mirroring WFE's
 * MaintenanceDateRangePicker presets:
 *  - TODAY: start-of-day .. end-of-day today
 *  - NEXT_WEEK: next ISO week, Monday 00:00 .. Sunday 23:59:59.999
 *  - NEXT_MONTH: next calendar month, day 1 00:00 .. last day 23:59:59.999
 *
 * Boundaries are computed in local time (as WFE did); callers serialize to UTC
 * instants when building the request.
 *
 * @param now injectable clock for testing; defaults to the current time.
 */
export const getPresetRange = (preset: ComputablePreset, now: Date = new Date()): DateRangeValue => {
    switch (preset) {
        case 'TODAY':
            return { from: startOfDay(now), to: endOfDay(now) };
        case 'NEXT_WEEK': {
            const nextWeekMonday = startOfIsoWeek(addDays(now, 7));
            return { from: nextWeekMonday, to: endOfDay(addDays(nextWeekMonday, 6)) };
        }
        case 'NEXT_MONTH': {
            const from = new Date(now.getFullYear(), now.getMonth() + 1, 1, 0, 0, 0, 0);
            // Day 0 of the month after `from` is the last day of `from`'s month.
            const to = new Date(from.getFullYear(), from.getMonth() + 1, 0, 23, 59, 59, 999);
            return { from, to };
        }
    }
};

/** Serializes a DayPicker date range to the ISO strings used by the request params. */
export const toIsoRange = (range: DateRangeValue): { from?: string; to?: string } => ({
    from: range.from?.toISOString(),
    to: range.to?.toISOString(),
});

/** Rebuilds a DayPicker date range from persisted/query ISO strings. */
export const fromIsoRange = (from?: string | null, to?: string | null): DateRangeValue => ({
    from: from ? new Date(from) : undefined,
    to: to ? new Date(to) : undefined,
});
